import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import { METRIC_COLORS, METRIC_LABELS } from './TaskResultLinksSection';

const AI_AGENTS = [
    { slug: 'analyst', name: 'Аналитик' },
    { slug: 'product', name: 'Товарный агент' },
    { slug: 'reference', name: 'Справочный агент' },
    { slug: 'seo', name: 'SEO-агент' },
    { slug: 'ads', name: 'Рекламный агент' },
    { slug: 'pricing', name: 'Агент цен и акций' },
    { slug: 'reviews', name: 'Агент отзывов' },
    { slug: 'visual', name: 'Визуальный агент' },
    { slug: 'fbs', name: 'FBS-агент' },
    { slug: 'dbs', name: 'DBS-агент' },
    { slug: 'fbo', name: 'FBO/FBW-агент' },
    { slug: 'pickup', name: 'Агент вывоза товара' },
    { slug: 'certificates', name: 'Агент сертификатов' },
    { slug: 'launch', name: 'Агент запуска товара' },
    { slug: 'support', name: 'Агент поддержки' },
];

function parseSkuText(raw) {
    return Array.from(
        new Set(
            String(raw || '')
                .split(/[\n,;\t\r ]+/)
                .map((s) => s.trim())
                .filter(Boolean)
        )
    );
}

function normalizeId(value) {
    return String(value || '').replace(/-/g, '').toLowerCase();
}

function TaskCreateModal({
    open,
    onClose,
    onSubmit,
    taskForm,
    setTaskForm,
    user,
    visibleWorkspaces,
    deals = [],
    taskTagsInput,
    setTaskTagsInput,
    handleTagsBlur,
    handleTagsKeyDown,
    companyUsers,
    getUserLabel,
    toggleTaskList,
    marketplaces,
    tasksBusy,
    panelRef,
    panelStyle,
    onResizeStart,
    request,
    showStatus,
}) {
    const [catalog, setCatalog] = useState([]);
    const [skuSearch, setSkuSearch] = useState('');
    const [skuResults, setSkuResults] = useState([]);
    const [searchBusy, setSearchBusy] = useState(false);
    const [skuPaste, setSkuPaste] = useState('');
    const [showPaste, setShowPaste] = useState(false);
    const searchWrapRef = useRef(null);
    const pasteWrapRef = useRef(null);

    const currentWorkspace = visibleWorkspaces.find(
        (workspace) => String(workspace.id) === String(taskForm.workspace_id)
    );
    const allowedUserIds = new Set((currentWorkspace?.members || []).map((member) => String(member.user_id)));
    const allowedCabinetIds = new Set(
        (currentWorkspace?.cabinets || []).map((cabinet) => String(cabinet.marketplace_account_id))
    );
    const filteredUsers = currentWorkspace
        ? companyUsers.filter(
              (item) =>
                  allowedUserIds.has(String(item.id)) || String(item.id) === String(user?.id || '')
          )
        : companyUsers;
    const filteredMarketplaces = currentWorkspace
        ? marketplaces.filter((account) => allowedCabinetIds.has(String(account.id)))
        : marketplaces;

    const workspaceCabinets = useMemo(() => {
        const byId = new Map((filteredMarketplaces || []).map((m) => [normalizeId(m.id), m]));
        return (currentWorkspace?.cabinets || [])
            .map((cabinet) => {
                const id = String(cabinet.marketplace_account_id || '');
                const account = byId.get(normalizeId(id));
                if (!account) return null;
                return {
                    id,
                    account_name: account?.account_name || id,
                    marketplace_type: account?.marketplace_type || '',
                };
            })
            .filter(Boolean);
    }, [currentWorkspace, filteredMarketplaces]);

    const metricOptions = useMemo(
        () => catalog.flatMap((g) => g.options || []),
        [catalog]
    );

    // Привязка к результату может ссылаться только на кабинет, выбранный слева для самой
    // задачи — иначе можно было привязать результат к кабинету, которого в задаче нет.
    const resultCabinetOptions = useMemo(() => {
        const selected = new Set((taskForm.cabinet_ids || []).map((id) => String(id)));
        return workspaceCabinets.filter((m) => selected.has(String(m.id)));
    }, [workspaceCabinets, taskForm.cabinet_ids]);

    useEffect(() => {
        if (!open) return;
        if (
            taskForm.result_cabinet_id &&
            !resultCabinetOptions.some((m) => String(m.id) === String(taskForm.result_cabinet_id))
        ) {
            setTaskForm((prev) => ({
                ...prev,
                result_cabinet_id: '',
                result_scope: 'assortment',
                result_skus: [],
            }));
        }
    }, [open, resultCabinetOptions, taskForm.result_cabinet_id, setTaskForm]);

    useEffect(() => {
        if (!open) return;
        const run = async () => {
            try {
                const catalogRes = await request('/api/v1/tasks/metric-options');
                setCatalog(catalogRes?.groups || []);
            } catch (e) {
                showStatus?.('error', `Не удалось загрузить показатели. ${e?.message || ''}`.trim());
            }
        };
        run();
    }, [open, request, showStatus]);

    useEffect(() => {
        if (!open) return;
        if (!taskForm.result_cabinet_id && resultCabinetOptions.length === 1) {
            setTaskForm((prev) => ({ ...prev, result_cabinet_id: resultCabinetOptions[0].id }));
        }
    }, [open, taskForm.result_cabinet_id, resultCabinetOptions, setTaskForm]);

    useEffect(() => {
        if (!open) return;
        if (!taskForm.result_cabinet_id || !skuSearch.trim() || taskForm.result_scope !== 'skus') {
            setSkuResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchBusy(true);
            try {
                const results = await request(
                    `/api/v1/marketplaces/${taskForm.result_cabinet_id}/products?q=${encodeURIComponent(skuSearch)}&limit=20`
                );
                setSkuResults(results || []);
            } catch (e) {
                setSkuResults([]);
                showStatus?.('error', `Ошибка поиска артикулов. ${e?.message || ''}`.trim());
            } finally {
                setSearchBusy(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [open, skuSearch, taskForm.result_cabinet_id, taskForm.result_scope, request, showStatus]);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSkuResults([]);
            }
            if (pasteWrapRef.current && !pasteWrapRef.current.contains(e.target)) {
                setShowPaste(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const addSku = (product) => {
        const sku = String(product?.article || product?.sku || '').trim();
        if (!sku) return;
        setTaskForm((prev) => ({
            ...prev,
            result_skus: Array.from(new Set([...(prev.result_skus || []), sku])),
        }));
        setSkuSearch('');
        setSkuResults([]);
    };

    const addPastedSkus = () => {
        const parsed = parseSkuText(skuPaste);
        if (!parsed.length) return;
        setTaskForm((prev) => ({
            ...prev,
            result_skus: Array.from(new Set([...(prev.result_skus || []), ...parsed])),
        }));
        setSkuPaste('');
    };

    const removeSku = (sku) => {
        setTaskForm((prev) => ({
            ...prev,
            result_skus: (prev.result_skus || []).filter((s) => s !== sku),
        }));
    };

    const toggleMetric = (key) => {
        setTaskForm((prev) => {
            const current = prev.result_metrics || [];
            if (key === 'all') {
                return { ...prev, result_metrics: current.includes('all') ? [] : ['all'] };
            }
            const withoutAll = current.filter((k) => k !== 'all');
            return withoutAll.includes(key)
                ? { ...prev, result_metrics: withoutAll.filter((k) => k !== key) }
                : { ...prev, result_metrics: [...withoutAll, key] };
        });
    };

    if (!open) return null;

    return (
        <Modal open onClose={onClose} title="Создать задачу" wide resizable panelRef={panelRef} panelStyle={panelStyle}>
            <form className="tasks__form" onSubmit={onSubmit}>
                <div className="tasks__body">
                    <div className="tasks__layout">
                        <div className="tasks__section">
                            {(deals.length > 0 || taskForm.deal_id) && (
                                <label>
                                    Сделка CRM
                                    <select
                                        value={taskForm.deal_id || ''}
                                        onChange={(event) => setTaskForm({ ...taskForm, deal_id: event.target.value })}
                                    >
                                        <option value="">Без сделки</option>
                                        {deals.map((deal) => (
                                            <option key={deal.id} value={deal.id}>{deal.title}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <label>
                                Проект
                                <select
                                    value={taskForm.workspace_id}
                                    onChange={(event) => {
                                        const workspaceId = event.target.value;
                                        const nextWorkspace = visibleWorkspaces.find(
                                            (workspace) => String(workspace.id) === String(workspaceId)
                                        );
                                        const nextAllowedUserIds = new Set(
                                            (nextWorkspace?.members || []).map((member) => String(member.user_id))
                                        );
                                        const nextAllowedCabinetIds = new Set(
                                            (nextWorkspace?.cabinets || []).map((cabinet) => String(cabinet.marketplace_account_id))
                                        );
                                        setTaskForm({
                                            ...taskForm,
                                            workspace_id: workspaceId,
                                            assignee_id: nextWorkspace && taskForm.assignee_id && !nextAllowedUserIds.has(String(taskForm.assignee_id))
                                                ? ''
                                                : taskForm.assignee_id,
                                            co_assignee_ids: nextWorkspace
                                                ? taskForm.co_assignee_ids.filter((id) => nextAllowedUserIds.has(String(id)))
                                                : taskForm.co_assignee_ids,
                                            watcher_ids: nextWorkspace
                                                ? taskForm.watcher_ids.filter((id) => nextAllowedUserIds.has(String(id)))
                                                : taskForm.watcher_ids,
                                            cabinet_ids: nextWorkspace
                                                ? taskForm.cabinet_ids.filter((id) => nextAllowedCabinetIds.has(String(id)))
                                                : taskForm.cabinet_ids,
                                            result_cabinet_id:
                                                nextWorkspace && taskForm.result_cabinet_id && !nextAllowedCabinetIds.has(String(taskForm.result_cabinet_id))
                                                    ? ''
                                                    : taskForm.result_cabinet_id,
                                            result_skus:
                                                nextWorkspace && taskForm.result_cabinet_id && !nextAllowedCabinetIds.has(String(taskForm.result_cabinet_id))
                                                    ? []
                                                    : taskForm.result_skus,
                                        });
                                    }}
                                >
                                    <option value="">Без проекта</option>
                                    {visibleWorkspaces.map((workspace) => (
                                        <option key={workspace.id} value={workspace.id}>
                                            {workspace.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                Тема
                                <input
                                    type="text"
                                    value={taskForm.title}
                                    onChange={(event) =>
                                        setTaskForm({ ...taskForm, title: event.target.value })
                                    }
                                    required
                                />
                            </label>
                            <label>
                                Описание
                                <textarea
                                    rows={12}
                                    value={taskForm.description}
                                    onChange={(event) =>
                                        setTaskForm({ ...taskForm, description: event.target.value })
                                    }
                                />
                            </label>
                            <div className="tasks__dates">
                                <label>
                                    Начало
                                    <input
                                        type="datetime-local"
                                        value={taskForm.start_at}
                                        onChange={(event) =>
                                            setTaskForm({ ...taskForm, start_at: event.target.value })
                                        }
                                    />
                                </label>
                                <label>
                                    Дата выполнения
                                    <input
                                        type="datetime-local"
                                        value={taskForm.due_at}
                                        onChange={(event) =>
                                            setTaskForm({ ...taskForm, due_at: event.target.value })
                                        }
                                    />
                                </label>
                            </div>
                            <label>
                                Теги (через запятую)
                                <input
                                    type="text"
                                    value={taskTagsInput}
                                    onChange={(event) => setTaskTagsInput(event.target.value)}
                                    onBlur={handleTagsBlur}
                                    onKeyDown={handleTagsKeyDown}
                                />
                            </label>
                            {taskForm.tags.length > 0 && (
                                <div className="tasks__tags">
                                    {taskForm.tags.map((tag) => (
                                        <span key={tag} className="chip">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="tasks__section">
                            <label>
                                Тип исполнителя
                                <select
                                    value={taskForm.assignee_type || 'user'}
                                    onChange={(event) => {
                                        const type = event.target.value;
                                        setTaskForm({
                                            ...taskForm,
                                            assignee_type: type,
                                            assignee_id: type === 'ai_agent' ? '' : taskForm.assignee_id,
                                            ai_agent_slug: type === 'user' ? '' : taskForm.ai_agent_slug,
                                        });
                                    }}
                                >
                                    <option value="user">Сотрудник</option>
                                    <option value="ai_agent">AI-агент</option>
                                </select>
                            </label>
                            {(taskForm.assignee_type || 'user') === 'user' ? (
                                <label>
                                    Главный исполнитель
                                    <select
                                        value={taskForm.assignee_id}
                                        onChange={(event) =>
                                            setTaskForm({ ...taskForm, assignee_id: event.target.value })
                                        }
                                    >
                                        <option value="">Не выбран</option>
                                        {filteredUsers.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {getUserLabel(item)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            ) : (
                                <label>
                                    AI-агент
                                    <select
                                        value={taskForm.ai_agent_slug || ''}
                                        onChange={(event) =>
                                            setTaskForm({ ...taskForm, ai_agent_slug: event.target.value })
                                        }
                                    >
                                        <option value="">Автовыбор по задаче</option>
                                        {AI_AGENTS.map((agent) => (
                                            <option key={agent.slug} value={agent.slug}>
                                                {agent.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {(taskForm.assignee_type || 'user') === 'user' && (
                                <div className="tasks__pick tasks__pick--scroll">
                                    <h4>Соисполнители</h4>
                                    <div className="tasks__options">
                                        {filteredUsers.map((item) => (
                                            <button
                                                type="button"
                                                key={item.id}
                                                className={`tasks__option ${
                                                    taskForm.co_assignee_ids.includes(item.id) ? 'active' : ''
                                                }`}
                                                onClick={() => toggleTaskList('co_assignee_ids', item.id)}
                                                aria-pressed={taskForm.co_assignee_ids.includes(item.id)}
                                            >
                                                {getUserLabel(item)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="tasks__pick tasks__pick--scroll">
                                <h4>Наблюдатели</h4>
                                <div className="tasks__options">
                                    {filteredUsers.map((item) => (
                                        <button
                                            type="button"
                                            key={item.id}
                                            className={`tasks__option ${
                                                taskForm.watcher_ids.includes(item.id) ? 'active' : ''
                                            }`}
                                            onClick={() => toggleTaskList('watcher_ids', item.id)}
                                            aria-pressed={taskForm.watcher_ids.includes(item.id)}
                                        >
                                            {getUserLabel(item)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="tasks__pick tasks__pick--scroll">
                                <h4>Кабинеты</h4>
                                <div className="tasks__options">
                                    {filteredMarketplaces.map((account) => (
                                        <button
                                            type="button"
                                            key={account.id}
                                            className={`tasks__option ${
                                                taskForm.cabinet_ids.includes(account.id) ? 'active' : ''
                                            }`}
                                            onClick={() => toggleTaskList('cabinet_ids', account.id)}
                                            aria-pressed={taskForm.cabinet_ids.includes(account.id)}
                                        >
                                            {account.account_name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tasks__result-links-wrap">
                        <div className="result-links">
                            <div className="result-links__head">
                                <span className="result-links__title">Привязка к результату</span>
                                {currentWorkspace && <span className="result-links__project-badge">{currentWorkspace.name}</span>}
                            </div>
                            <div className="result-links__form">
                                {resultCabinetOptions.length === 0 ? (
                                    <p className="result-links__sku-hint">
                                        Сначала выберите кабинет(ы) для задачи слева — привязать результат
                                        можно только к одному из них
                                    </p>
                                ) : (
                                    <div className="result-links__row">
                                        <select
                                            value={taskForm.result_cabinet_id || ''}
                                            onChange={(e) => setTaskForm({ ...taskForm, result_cabinet_id: e.target.value })}
                                            className="result-links__select"
                                        >
                                            <option value="">Выберите кабинет</option>
                                            {resultCabinetOptions.map((m) => (
                                                <option key={m.id} value={m.id}>
                                                    {m.account_name}
                                                    {m.marketplace_type === 'wildberries' ? ' (WB)' : ' (OZON)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="result-links__scope">
                                    <label className="result-links__radio">
                                        <input
                                            type="radio"
                                            name="create-rl-scope"
                                            value="assortment"
                                            checked={(taskForm.result_scope || 'assortment') === 'assortment'}
                                            onChange={() => setTaskForm({ ...taskForm, result_scope: 'assortment' })}
                                            disabled={!taskForm.result_cabinet_id}
                                        />
                                        Весь ассортимент
                                    </label>
                                    <label className="result-links__radio">
                                        <input
                                            type="radio"
                                            name="create-rl-scope"
                                            value="skus"
                                            checked={taskForm.result_scope === 'skus'}
                                            onChange={() => setTaskForm({ ...taskForm, result_scope: 'skus' })}
                                            disabled={!taskForm.result_cabinet_id}
                                        />
                                        Группа артикулов
                                    </label>
                                </div>

                                {taskForm.result_scope === 'skus' && (
                                    <div className="result-links__sku-picker">
                                        {!taskForm.result_cabinet_id ? (
                                            <p className="result-links__sku-hint">
                                                Выберите конкретный кабинет для поиска артикулов
                                            </p>
                                        ) : (
                                            <div className="result-links__sku-search-wrap" ref={searchWrapRef}>
                                                <input
                                                    type="text"
                                                    className="result-links__sku-input"
                                                    value={skuSearch}
                                                    onChange={(e) => setSkuSearch(e.target.value)}
                                                    placeholder="Поиск артикула или названия товара..."
                                                />
                                                {searchBusy && <span className="result-links__sku-busy">Поиск...</span>}
                                                {skuResults.length > 0 && (
                                                    <ul className="result-links__sku-results">
                                                        {skuResults.map((p) => (
                                                            <li
                                                                key={p.article || p.sku}
                                                                className="result-links__sku-result"
                                                                onMouseDown={() => addSku(p)}
                                                            >
                                                                <b>{p.article || p.sku}</b>
                                                                {p.vendor_article ? ` (арт. ${p.vendor_article})` : ''}
                                                                {p.name ? ` - ${p.name}` : ''}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        )}

                                        <div className="result-links__row" style={{ marginTop: 8 }} ref={pasteWrapRef}>
    <button
        type="button"
        className="result-links__add-btn"
        onClick={() => setShowPaste((prev) => !prev)}
    >
        Вставить столбец артикулов
    </button>
    {showPaste && (
        <div className="result-links__sku-results" style={{ width: '100%', maxHeight: 'none', position: 'static' }}>
            <textarea
                rows={3}
                className="result-links__sku-input"
                value={skuPaste}
                onChange={(e) => setSkuPaste(e.target.value)}
                placeholder="Артикулы по одному в строке или через разделитель"
            />
            <button
                type="button"
                className="result-links__add-btn"
                onClick={addPastedSkus}
                style={{ marginTop: 8 }}
            >
                Добавить артикулы
            </button>
        </div>
    )}
</div>
{(taskForm.result_skus || []).length > 0 && (
                                            <div className="result-links__sku-selected">
                                                {(taskForm.result_skus || []).map((sku) => (
                                                    <span key={sku} className="result-links__sku-tag" title={sku}>
                                                        {sku}
                                                        <button type="button" onClick={() => removeSku(sku)}>
                                                            x
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="result-links__metrics">
                                    <span className="result-links__metrics-label">Показатели:</span>
                                    <div className="result-links__metrics-pills">
                                        {metricOptions.map((option) => {
                                            const key = option.key;
                                            const label = option.label || METRIC_LABELS[key] || key;
                                            const active = (taskForm.result_metrics || []).includes(key);
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className={`result-links__metric-pill${active ? ' active' : ''}`}
                                                    style={
                                                        active
                                                            ? {
                                                                  borderColor: METRIC_COLORS[key] || '#6b7280',
                                                                  background: `${METRIC_COLORS[key] || '#6b7280'}22`,
                                                                  color: METRIC_COLORS[key] || '#6b7280',
                                                              }
                                                            : {}
                                                    }
                                                    onClick={() => toggleMetric(key)}
                                                    disabled={!taskForm.result_cabinet_id}
                                                >
                                                    <span
                                                        className="result-links__metric-dot"
                                                        style={{ background: METRIC_COLORS[key] || '#6b7280' }}
                                                    />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="tasks__actions">
                    <button type="button" className="ghost" onClick={onClose}>
                        Отмена
                    </button>
                    <button type="submit" disabled={tasksBusy}>
                        Сохранить
                    </button>
                </div>
            </form>
            <div className="modal__resizer" role="presentation" onMouseDown={onResizeStart} />
        </Modal>
    );
}

export default TaskCreateModal;


